import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase, SUPABASE_ENABLED, type AppRole } from '@/lib/supabaseClient'

interface User {
  id: string
  email: string
  role: AppRole | 'client' | 'partner' | 'admin'
  accessLevel: 'basic' | 'premium' | 'exclusive'
  name?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  hasAccess: (requiredLevel: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Demo users for mock mode (no Supabase envs)
  const mockUsers: Record<string, { password: string; user: User }> = {
    'client@novahass.partners': {
      password: 'demo123',
      user: {
        id: '1',
        email: 'client@novahass.partners',
        name: 'Demo Client',
        role: 'client',
        accessLevel: 'basic'
      }
    },
    'partner@novahass.partners': {
      password: 'demo123',
      user: {
        id: '2',
        email: 'partner@novahass.partners',
        name: 'Demo Partner',
        role: 'partner',
        accessLevel: 'premium'
      }
    },
    'admin@novahass.partners': {
      password: 'demo123',
      user: {
        id: '3',
        email: 'admin@novahass.partners',
        name: 'Demo Admin',
        role: 'admin',
        accessLevel: 'exclusive'
      }
    }
  }

  // Session initialization
  useEffect(() => {
    let isMounted = true

    const initMock = () => {
      const storedUser = localStorage.getItem('novahass_user')
      if (!isMounted) return
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser))
        } catch {
          localStorage.removeItem('novahass_user')
        }
      }
      setIsLoading(false)
    }

    const initSupabase = async () => {
      const { data } = await supabase.auth.getSession()
      if (!isMounted) return
      const session = data.session
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id,email,role,display_name')
          .eq('id', session.user.id)
          .single()
        const accessLevel = (profile?.role as User['accessLevel']) || 'basic'
        setUser({ id: session.user.id, email: session.user.email || '', role: profile?.role || 'basic', accessLevel, name: profile?.display_name || undefined })
      }
      setIsLoading(false)
    }

    if (!SUPABASE_ENABLED) {
      initMock()
      return () => {
        isMounted = false
      }
    }

    initSupabase()

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        setUser(null)
        return
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('id,email,role,display_name')
        .eq('id', session.user.id)
        .single()
      const accessLevel = (profile?.role as User['accessLevel']) || 'basic'
      setUser({ id: session.user.id, email: session.user.email || '', role: profile?.role || 'basic', accessLevel, name: profile?.display_name || undefined })
    })

    return () => {
      isMounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true)

    if (!SUPABASE_ENABLED) {
      await new Promise((r) => setTimeout(r, 400))
      const mockUser = mockUsers[email]
      if (mockUser && mockUser.password === password) {
        setUser(mockUser.user)
        localStorage.setItem('novahass_user', JSON.stringify(mockUser.user))
        setIsLoading(false)
        return true
      }
      setIsLoading(false)
      return false
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error || !data.session) {
      setIsLoading(false)
      return false
    }
    setIsLoading(false)
    return true
  }

  const logout = async () => {
    if (!SUPABASE_ENABLED) {
      setUser(null)
      localStorage.removeItem('novahass_user')
      return
    }
    await supabase.auth.signOut()
    setUser(null)
  }

  const hasAccess = (requiredLevel: string): boolean => {
    if (!user) return false
    const accessLevels = { basic: 1, premium: 2, exclusive: 3 }
    const userLevel = accessLevels[user.accessLevel] || 0
    const required = accessLevels[requiredLevel as keyof typeof accessLevels] || 0
    return userLevel >= required
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasAccess
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
